import { BOARD_SIZE, opponentOf, progressAt, type GameState, type Move } from "../game";
import { generateLegalMovesUnchecked } from "../game/rules";
import { applyGeneratedMove } from "../game/searchTransition";

function isWinningTransition(
  state: GameState,
  move: Move,
  player: GameState["activePlayer"],
): boolean {
  return move.kind === "passage" || state.outcome?.winner === player;
}

function allowsImmediateLoss(afterMove: GameState, player: GameState["activePlayer"]): boolean {
  if (afterMove.outcome !== null) return afterMove.outcome.winner !== player;
  return generateLegalMovesUnchecked(afterMove).some((reply) =>
    isWinningTransition(applyGeneratedMove(afterMove, reply).state, reply, afterMove.activePlayer),
  );
}

function createsForcedBackRowClimbAfter(
  state: GameState,
  move: Move,
  afterMove: GameState,
): boolean {
  if (move.kind !== "normal") return false;
  const destination = state.cells[move.to.row * BOARD_SIZE + move.to.column];
  if (destination?.kind !== "field" || destination.stack.length !== 1) return false;

  if (afterMove.outcome !== null) return false;
  const attacker = afterMove.activePlayer;
  if (progressAt(attacker, move.to) !== BOARD_SIZE - 1) return false;

  return generateLegalMovesUnchecked(afterMove).some((climb) => {
    if (
      climb.kind !== "normal" ||
      climb.to.row !== move.to.row ||
      climb.to.column !== move.to.column
    ) {
      return false;
    }

    const afterClimb = applyGeneratedMove(afterMove, climb).state;
    if (afterClimb.outcome !== null) return afterClimb.outcome.winner === attacker;
    if (afterClimb.activePlayer !== opponentOf(attacker)) return false;

    return generateLegalMovesUnchecked(afterClimb).every((reply) => {
      const afterReply = applyGeneratedMove(afterClimb, reply).state;
      if (afterReply.outcome !== null) return afterReply.outcome.winner === attacker;
      return generateLegalMovesUnchecked(afterReply).some(
        (attackerMove) => attackerMove.kind === "passage",
      );
    });
  });
}

export function createsForcedBackRowClimb(state: GameState, move: Move): boolean {
  return createsForcedBackRowClimbAfter(state, move, applyGeneratedMove(state, move).state);
}

/**
 * Keeps emergency recovery deterministic and cheap while avoiding obvious tactical
 * concessions. This is intentionally bounded pattern checking, not main-thread search.
 */
export function selectBotFallbackMove(state: GameState): Move | null {
  const moves = generateLegalMovesUnchecked(state);
  const evaluated = moves.map((move) => ({
    move,
    afterMove: applyGeneratedMove(state, move).state,
  }));
  const winning = evaluated.find(({ move, afterMove }) =>
    isWinningTransition(afterMove, move, state.activePlayer),
  );
  if (winning !== undefined) return winning.move;

  let firstAvoidingImmediateLoss: Move | null = null;
  for (const candidate of evaluated) {
    if (allowsImmediateLoss(candidate.afterMove, state.activePlayer)) continue;
    firstAvoidingImmediateLoss ??= candidate.move;
    if (!createsForcedBackRowClimbAfter(state, candidate.move, candidate.afterMove)) {
      return candidate.move;
    }
  }

  return firstAvoidingImmediateLoss ?? moves[0] ?? null;
}
